import { createApi } from "@reduxjs/toolkit/query/react";
import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";
import GETJwtToken from "../authentication/authentication";

/** Cloud-derived display status; unreported inventory is informational. */
export type LiftWatchSeverity = "healthy" | "warning" | "critical" | "info";
/** Connectivity is derived from cloud receipt time, never the appliance clock. */
export type LiftWatchConnection = "online" | "offline" | "awaiting_first_report";

/** One active server with nullable last-known diagnostic measurements. */
export interface LiftWatchServer {
  server_id: string;
  name: string;
  org_name: string;
  org_id: string;
  location: string | null;
  overall_status: LiftWatchSeverity;
  online: boolean;
  last_seen: string | null;
  connection: LiftWatchConnection;
  issue: { title: string; severity: LiftWatchSeverity; detail: string; code?: string };
  software: {
    agent: string | null;
    latest: string | null;
    os: string | null;
    current: boolean | null;
  };
  services: { running: number | null; total: number | null; uptime_30d_pct: number | null };
  security_summary: string | null;
  error_samples: { ts: string; service: string; line: string }[];
}

/** Complete active inventory and the fleet-wide aggregates for one snapshot. */
export interface LiftWatchFleetResponse {
  snapshot_at: string;
  health: {
    total: number;
    healthy: number;
    warning: number;
    critical: number;
    accounts: number;
    awaiting_first_report: number;
  };
  metrics: {
    connected: { count: number; total: number; offline: number };
    versions: {
      up_to_date: number;
      total: number;
      behind: number;
      latest: string | null;
      unknown: number;
    };
    security: { open_incidents: number; high_severity: number };
    uptime_30d: { fleet_pct: number | null; target_pct: number };
    errors_24h: { count: number | null; servers: number; top_server: string | null };
  };
  servers: LiftWatchServer[];
}

const fleetBaseQuery = createFlatBufferBaseQuery(
  process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
);
// A transport failure cannot re-authorize a snapshot rejected by the server.
let authorizationFailure: FetchBaseQueryError | undefined;

export const liftWatchAPI = createApi({
  reducerPath: "liftWatchAPI",
  // Resolve the current session on every poll, including long-lived tabs.
  baseQuery: async (args: FetchArgs, api, extraOptions) => {
    try {
      const token = await GETJwtToken();
      const result = await fleetBaseQuery(
        { ...args, headers: { authorization: token } },
        api,
        extraOptions,
      );
      if (!result.error) authorizationFailure = undefined;
      else if (result.error.status === 401 || result.error.status === 403)
        authorizationFailure = result.error;
      return authorizationFailure ? { error: authorizationFailure } : result;
    } catch {
      authorizationFailure = { status: 401, data: { message: "Authentication required" } };
      return { error: authorizationFailure };
    }
  },
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getLiftWatchFleet: builder.query<LiftWatchFleetResponse, void>({
      query: () => ({
        url: "/liftwatch/fleet",
        // This endpoint is intentionally JSON-only, without an fbSerializer.
        responseHandler: (response: Response) => response.json(),
      }),
    }),
  }),
});

export const { useGetLiftWatchFleetQuery } = liftWatchAPI;

/** Poll only for authorized viewers and never expose a denied cached snapshot. */
export function useLiftWatchFleetQuery(authorized: boolean) {
  const result = useGetLiftWatchFleetQuery(undefined, {
    skip: !authorized,
    pollingInterval: 60_000,
  });
  const forbidden =
    result.error &&
    "status" in result.error &&
    (result.error.status === 401 || result.error.status === 403);
  return { ...result, data: authorized && !forbidden ? result.data : undefined };
}
